import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageShell, DataTable, type DataTableColumn, RouteActionButton } from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import psychosocialService from '../../services/psychosocialService';
import { PsychosocialForm } from '../../types/psychosocial';
import { getApiErrorMessage } from '../../types/common';

interface Props {
  embedded?: boolean;
}

const columns: DataTableColumn<PsychosocialForm>[] = [
  {
    id: 'name',
    label: 'Nome',
    primary: true,
    render: (form) => (
      <>
        <Typography fontWeight="medium">{form.name}</Typography>
        {form.description && (
          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
            {form.description}
          </Typography>
        )}
      </>
    ),
  },
  {
    id: 'baseTemplate',
    label: 'Template Base',
    render: (form) => (
      <Chip
        size="small"
        label={form.baseTemplate ? `${form.baseTemplate.name}` : 'N/A'}
        color={form.baseTemplate?.type === 'medium' ? 'primary' : 'default'}
      />
    ),
  },
  {
    id: 'createdAt',
    label: 'Criado em',
    render: (form) => new Date(form.createdAt).toLocaleDateString('pt-BR'),
  },
];

const PsychosocialFormsPage: React.FC<Props> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const excluir = useAcao('psychosocial_form.delete', 'Excluir');
  const editar = useControle('/psychosocial/forms/:id/edit', pode('psychosocial_form.update'), 'Editar');

  const [forms, setForms] = useState<PsychosocialForm[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState<PsychosocialForm | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Mantém a busca fora das dependências: ela só dispara no Enter/handleSearch.
  const searchRef = useRef(search);
  searchRef.current = search;

  const loadForms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await psychosocialService.getForms({
        page: page + 1,
        limit: rowsPerPage,
        search: searchRef.current || undefined,
      });
      setForms(result.data);
      setTotalCount(result.pagination?.total || 0);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao carregar formulários'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  const handleSearch = () => {
    setPage(0);
    loadForms();
  };

  const handleDelete = async () => {
    if (!formToDelete) return;
    setDeleting(true);
    try {
      await psychosocialService.deleteForm(formToDelete.id);
      enqueueSnackbar('Formulário removido com sucesso', { variant: 'success' });
      setDeleteDialogOpen(false);
      setFormToDelete(null);
      loadForms();
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'Erro ao remover formulário'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const content = (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar formulários..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        <RouteActionButton
          para="/psychosocial/forms/new"
          exige={pode('psychosocial_form.create')}
          variant="contained"
          startIcon={<AddIcon />}
        >
          Novo Formulário
        </RouteActionButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <DataTable
        columns={columns}
        rows={forms}
        getRowId={(form) => form.id}
        loading={loading}
        emptyState={{
          title: 'Nenhum formulário encontrado',
          description:
            'Formulários guardam as perguntas aplicadas nas campanhas psicossociais e são criados aqui.',
        }}
        onRowClick={
          editar.disabled ? undefined : (form) => navigate(`/psychosocial/forms/${form.id}/edit`)
        }
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            onClick: (form) => navigate(`/psychosocial/forms/${form.id}/edit`),
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: (form) => {
              setFormToDelete(form);
              setDeleteDialogOpen(true);
            },
            ...excluir,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount,
          onPageChange: setPage,
          onRowsPerPageChange: (n) => { setRowsPerPage(n); setPage(0); },
          rowsPerPageOptions: [5, 10, 25],
        }}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o formulário "{formToDelete?.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" disabled={deleting}>
            {deleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (embedded) return content;

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Psicossocial', path: '/psychosocial' },
        { label: 'Formulários' },
      ]}
      title="Formulários Psicossociais"
      subtitle="Gerencie os formulários de avaliação psicossocial"
    >
      {content}
    </PageShell>
  );
};

export default PsychosocialFormsPage;
